import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Route, Switch } from 'react-router-dom';

import { IProps } from './type';

import HelmetNoIndex from '../../shared/components/helmet-no-index';
import HelmetIcon from '../../shared/components/helmet-icon';
import HelpscoutButton from '../../shared/components/helpscout-button';

import Login from './components/login';
import SSOLogin from './components/sso-login';
import Signup from './components/signup';
import ResetPassword from './components/reset-password';
import ChangePassword from './components/change-password';
import AcceptInvitationSignup from './components/invitation-signup';
import HelpscoutBeaconEvents from '../../shared/enums/helpscout-beacon-events';
import LTDSignup from './components/ltd-signup';
import LTDCheckout from './components/ltd-checkout';
import AgencyClientLogin from './components/agency-client-login';

import {
  destroyChatSupport,
  initializeIntercom,
} from '../../shared/utils/helpscout-handlers';
import { openIntercomHome } from '../../shared/utils/open-chat-support';
import { isWhiteLabel } from '../../shared/utils/user-details';
import { getIsRequestPending } from '../../shared/utils';
import { isInternalUrl } from '../../shared/utils/is-internal-url';
import ssoCallback from './components/sso-callback';
import MFAVerify from './components/mfa-verify';
import MCPAuthSuccess from './components/mcp-auth-success';
import ThirdPartyIntegrations from '../../shared/components/third-party-integrations';

const Auth: React.FC<IProps> = ({
  sendGetAgencyConfigRequest,
  agencyConfig,
  location,
  agencyConfigRequestStatus,
}) => {
  const currentPath = location?.pathname?.toLowerCase() || '';
  const isAgencyLoginPage = currentPath === '/agency/login';
  const isMCPAuthSuccessPage = currentPath === '/mcp-auth-success';

  const agencyConfigPayload = {
    baseUrl: window.location.origin,
  };

  useEffect(() => {
    sendGetAgencyConfigRequest(agencyConfigPayload);
  }, []);

  useEffect(() => {
    if (
      window?.location &&
      agencyConfig?.showChatSupport &&
      !isAgencyLoginPage &&
      !isMCPAuthSuccessPage
    ) {
      initializeIntercom();

      setTimeout(() => {
        const params = new URLSearchParams(location.search);
        const chatOpen = params.get('chatopen');
        if (chatOpen === 'true') {
          openIntercomHome();
        }
      }, 1000);
    }

    return () => {
      destroyChatSupport();
    };
  }, [agencyConfig, isAgencyLoginPage, isMCPAuthSuccessPage, location.search]);

  useEffect(() => {
    if (window.Beacon) {
      window.Beacon('event', {
        type: HelpscoutBeaconEvents.PAGE_VIEWED,
        url: document.location.href,
        title: document.title,
      });
      window.Beacon(HelpscoutBeaconEvents.SUGGEST);
    }
  }, [window.Beacon, document.location.href]);

  const containerClassName = classNames([
    'auth',
    {
      'ltd-container':
        window.location?.pathname?.toLowerCase() === '/ltd-signup' ||
        window.location?.pathname?.toLowerCase() === '/ltd-checkout',
      'mcp-auth-success-layout': isMCPAuthSuccessPage,
    },
  ]);

  return (
    <div className={containerClassName}>
      <HelmetNoIndex />

      <HelmetIcon />

      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/sso-login" component={SSOLogin} />
        <Route path="/sso-callback" component={ssoCallback} />
        <Route path="/agency/login" component={AgencyClientLogin} />
        <Route path="/signup" component={Signup} />
        <Route path="/LTD-Signup" component={LTDSignup} />
        <Route path="/LTD-Checkout" component={LTDCheckout} />
        <Route path="/mfa-verify" component={MFAVerify} />
        <Route path="/mcp-auth-success" component={MCPAuthSuccess} />
        <Route exact path="/reset-password/:code" component={ChangePassword} />
        <Route
          exact
          path="/accept-invite/:token"
          component={AcceptInvitationSignup}
        />
        <Route exact path="/reset-password" component={ResetPassword} />
      </Switch>

      <ThirdPartyIntegrations enableHighAdvocacy={false} />

      {agencyConfig?.showChatSupport &&
        (!isWhiteLabel() || isInternalUrl()) &&
        !getIsRequestPending(agencyConfigRequestStatus) &&
        !isMCPAuthSuccessPage && <HelpscoutButton />}
    </div>
  );
};

export default Auth;
